import React from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps } from 'react-native'
import {
  AccessType,
  useCompany,
  useFloor,
  useWorkspace,
  useWorkspaceSchedule,
  useWorkspaceTriggers,
  useWorkspaceType,
  useZones,
} from 'zens-store'
import { Button, Color, Hyperlink, Radius, Spacing, Text } from 'zens-storybook'
import { FloorField, WorkspaceZones, InfoTable, InfoBlock, TimeZoneConflict } from '@dms/component'
import { Link } from 'zens-router'
import { usePermissions } from '@dms/hook'
import { ProductConnectivity, ProductOccupancy, ProductPeripherals } from 'zens-fragment'

import { ProgramType } from '../schedule/legend'

import WorkspaceProgram from './program'
import WorkspaceProduct from './product'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  block: {
    marginBottom: Spacing.M,
  },
  location: {
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  program: {
    maxWidth: 600,
  },
  skeletonStatus: {
    height: 38,
    justifyContent: 'flex-start',
  },
  skeletonTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  skeletonRow: {
    height: 32,
  },
  skeletonSchedule: {
    backgroundColor: Color.sleepingGrey,
    borderRadius: Radius.S,
    height: 164,
    marginBottom: Spacing.L,
  },
  floor: {
    flexDirection: 'row',
  },
  floorplan: {
    marginLeft: Spacing.S,
  },
  status: {
    flexDirection: 'row',
  },
})

interface Props extends ViewProps {
  companyId: number
  workspaceId: number
  programTypes?: ProgramType[]
  onChangeProgramTypes?: (types: ProgramType[]) => void
}
const WorkspaceInfo = (props: Props) => {
  const { companyId, workspaceId, programTypes, onChangeProgramTypes, style, ...otherProps } = props
  const { t } = useTranslation()
  const workspace = useWorkspace({ companyId, workspaceId })
  const company = useCompany({ companyId })
  const {
    workspaceTypeId = 0,
    floorId = 0,
    name,
    occupancyState,
    connectivityState,
    peripheralsState,
  } = workspace.data || {}
  // FIXME This should be a single query, API needs to be updated
  const floor = useFloor({ companyId, floorId })
  const buildingId = floor.data?.buildingId

  const schedule = useWorkspaceSchedule({ companyId, workspaceId })
  const triggers = useWorkspaceTriggers({ companyId, workspaceId }, { enabled: !!workspace.data?.product })
  const workspaceType = useWorkspaceType({ workspaceTypeId })
  const zones = useZones({ companyId, workspaceId })
  const { hasPermission } = usePermissions(companyId)

  const configStatus = {
    fields: [
      {
        label: t('Occupancy'),
        value: (
          <View style={styles.status}>
            <ProductOccupancy value={occupancyState} />
          </View>
        ),
      },
      {
        label: t('Connectivity'),
        value: (
          <View style={styles.status}>
            <ProductConnectivity value={connectivityState} />
          </View>
        ),
      },
      {
        label: t('Peripherals'),
        value: (
          <View style={styles.status}>
            <ProductPeripherals value={peripheralsState} />
          </View>
        ),
      },
    ],
  }

  const configDetail = {
    fields: [
      {
        label: t('Name'),
        value: name,
      },
      {
        label: t('Type'),
        value: workspaceType.data?.name,
      },
      {
        label: t('Located at'),
        value: (
          <View style={styles.floor}>
            <FloorField companyId={companyId} value={floorId} style={styles.location} />
            {/* FIXME: Search data does not have floorplan data */}
            {/* {workspace.data?.floorplanIsPlaced && ( */}
            <Link href={`/companies/${companyId}/workspaces/${workspaceId}/floorplan`} asChild>
              <Hyperlink title={t('Show on floorplan')} style={styles.floorplan} testID="WorkspaceInfo.FloorplanLink" />
            </Link>
            {/* )} */}
          </View>
        ),
      },
    ],
  }
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <InfoTable title={t('Status')} companyId={companyId} style={styles.block} config={configStatus} />
      <InfoTable
        title={t('Workspace details')}
        companyId={companyId}
        style={styles.block}
        config={configDetail}
        buttonHref={
          hasPermission(AccessType.Update, 'workspace')
            ? `/companies/${companyId}/workspaces/${workspaceId}/edit`
            : undefined
        }
      />
      <InfoBlock title={t('Week schedule')} style={styles.block}>
        <WorkspaceProgram
          schedule={schedule.data?.visual_program}
          triggers={triggers.data}
          colorScheme={company.data!.colorScheme}
          style={styles.program}
          types={programTypes}
          onChangeTypes={onChangeProgramTypes}
        />
        <TimeZoneConflict companyId={companyId} buildingId={buildingId} />
      </InfoBlock>
      <InfoBlock title={t('Connected device')} style={styles.block}>
        <WorkspaceProduct companyId={companyId} workspace={workspace.data} />
      </InfoBlock>
      <InfoBlock title={`${t('Zones')} (${zones.data?.length || 0})`} style={styles.block}>
        <WorkspaceZones companyId={companyId} zones={zones.data} />
      </InfoBlock>
    </View>
  )
}

interface SkeletonProps extends ViewProps {}

const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <InfoBlock.Skeleton title={t('Status')} style={styles.block}>
        <Text.Skeleton style={styles.skeletonStatus} />
        <Text.Skeleton />
      </InfoBlock.Skeleton>
      <InfoBlock.Skeleton
        title={
          <View style={styles.skeletonTitle}>
            <Text level={4}>{t('Workspace details')}</Text>
            <Button.Skeleton />
          </View>
        }
        style={styles.block}
      >
        <Text.Skeleton style={styles.skeletonRow} />
        <Text.Skeleton style={styles.skeletonRow} />
        <Text.Skeleton style={styles.skeletonRow} />
      </InfoBlock.Skeleton>
      <InfoBlock.Skeleton title={t('Connected devices')} style={styles.block}>
        <Text.Skeleton />
      </InfoBlock.Skeleton>
      <InfoBlock.Skeleton title={t('Week schedule')} style={styles.block}>
        <View style={styles.skeletonSchedule} />
      </InfoBlock.Skeleton>
      <InfoBlock.Skeleton title={t('Connected device')} style={styles.block}>
        <Text.Skeleton />
      </InfoBlock.Skeleton>
      <InfoBlock.Skeleton title={t('Zones')} style={styles.block}>
        <Text.Skeleton />
      </InfoBlock.Skeleton>
    </View>
  )
}
WorkspaceInfo.Skeleton = Skeleton

export default WorkspaceInfo
